import { plateFont } from '../../utils/fonts';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { withTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress,
  Snackbar,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  TireRepair as TireRepairIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useUserStore } from 'store';
import { getCsrfToken } from '../../utils';
import {
  useListPreferences,
  FilterButton,
  FilterPanel,
  FilterSection,
  FilterChip,
  SearchInput,
  FilterSummary,
} from '../shared/ListFilters';
import { usePaginatedList } from '../../hooks/usePaginatedList';
import PaginationControls from '../shared/PaginationControls';
import DataTable from '../shared/DataTable';
import TrialFeatureNotice from '../shared/TrialFeatureNotice';
import TireHotelCardForm from './TireHotelCardForm';
import NeedsReplacementChip from './NeedsReplacementChip';

const STORAGE_KEY = 'tire_hotel_cards_list_preferences';

const STATUSES = ['stored', 'on_vehicle', 'disposed'];
const TIRE_SET_TYPES = ['summer', 'winter', 'all_season'];

const defaultPreferences = {
  sortField: 'updated_at',
  sortDirection: 'desc',
  filters: {
    status: '',
    tire_set_type: '',
    needs_replacement: false,
  },
  searchQuery: '',
};

const getStatusColor = (status) => {
  switch (status) {
    case 'stored': return 'success';
    case 'on_vehicle': return 'info';
    case 'disposed': return 'default';
    default: return 'default';
  }
};

function TireHotelCardsPage({ t }) {
  const navigate = useNavigate();
  const { currentOrganization } = useUserStore();

  // Context menu state
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  // Dialog states
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [successSnackbar, setSuccessSnackbar] = useState(null);

  // Capacity stats
  const [capacity, setCapacity] = useState({ total: 0, occupied: 0, available: 0 });

  const {
    preferences,
    updatePreferences,
    updateFilters: updateLocalFilters,
    resetFilters: resetLocalFilters,
    handleSort: handleLocalSort,
    filtersOpen,
    toggleFiltersOpen,
  } = useListPreferences(STORAGE_KEY, defaultPreferences);

  // Before 2026-09 "Uusittava" was a status value. A filter saved on it would now send a
  // status the API no longer knows and show an empty list, so it becomes the flag filter.
  useEffect(() => {
    if (preferences.filters.status === 'flagged_replacement') {
      updateLocalFilters({ status: '', needs_replacement: true });
    }
  }, [preferences.filters.status, updateLocalFilters]);

  // Build server-side filter params from local preferences
  const serverFilters = useMemo(() => {
    const filters = {};
    if (preferences.filters.status) {
      filters.status = preferences.filters.status;
    }
    if (preferences.filters.tire_set_type) {
      filters.tire_set_type = preferences.filters.tire_set_type;
    }
    if (preferences.filters.needs_replacement) {
      filters.needs_replacement = 'true';
    }
    return filters;
  }, [preferences.filters]);

  const {
    data: items,
    loading,
    error,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
    searchQuery,
    setSearchQuery,
    setFilters: setServerFilters,
    setSort,
    refetch,
  } = usePaginatedList('/api/tire-hotel-cards/', {
    initialFilters: serverFilters,
    defaultSortField: preferences.sortField,
    defaultSortDirection: preferences.sortDirection,
    initialSearchQuery: preferences.searchQuery || '',
  });

  // Sync local preferences to server filters
  useEffect(() => {
    setServerFilters(serverFilters);
  }, [serverFilters, setServerFilters]);

  // Sync sort from local preferences to server
  useEffect(() => {
    setSort(preferences.sortField, preferences.sortDirection);
  }, [preferences.sortField, preferences.sortDirection, setSort]);

  // Sync search query from preferences to hook
  useEffect(() => {
    setSearchQuery(preferences.searchQuery);
  }, [preferences.searchQuery, setSearchQuery]);

  const handleSort = useCallback((field) => {
    handleLocalSort(field);
  }, [handleLocalSort]);

  const updateFilters = useCallback((newFilters) => {
    updateLocalFilters(newFilters);
  }, [updateLocalFilters]);

  const resetFilters = useCallback(() => {
    resetLocalFilters();
  }, [resetLocalFilters]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (preferences.filters.status) count++;
    if (preferences.filters.tire_set_type) count++;
    if (preferences.filters.needs_replacement) count++;
    return count;
  }, [preferences.filters]);

  // Build filter summary chips
  const filterSummaryChips = useMemo(() => {
    const chips = [];
    if (preferences.filters.status) {
      chips.push({
        key: 'status',
        label: t(`tire_hotel.${preferences.filters.status}`),
      });
    }
    if (preferences.filters.tire_set_type) {
      chips.push({
        key: 'tire_set_type',
        label: t(`tire_hotel.${preferences.filters.tire_set_type}`),
      });
    }
    if (preferences.filters.needs_replacement) {
      chips.push({
        key: 'needs_replacement',
        label: t('tire_hotel.needs_replacement_only'),
      });
    }
    return chips;
  }, [preferences.filters, t]);

  // Fetch capacity stats
  const fetchCapacity = useCallback(async () => {
    if (!currentOrganization?.organization_id) return;
    try {
      const response = await axios.get('/api/tire-hotel-cards/capacity/', {
        headers: { 'X-Organization-Id': currentOrganization.organization_id },
      });
      setCapacity({
        total: response.data.total || 0,
        occupied: response.data.occupied || 0,
        available: response.data.available || 0,
        capacityAlert: response.data.capacity_alert || false,
        occupancyPercentage: response.data.occupancy_percentage || 0,
        alertThreshold: response.data.alert_threshold || 90,
      });
    } catch (err) {
      console.error('Failed to fetch capacity:', err);
    }
  }, [currentOrganization?.organization_id]);

  useEffect(() => {
    fetchCapacity();
  }, [fetchCapacity]);

  const handleContextMenu = (event, item) => {
    event.stopPropagation();
    setSelectedItem(item);
    setContextMenu({ mouseX: event.clientX, mouseY: event.clientY });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  // Delete card
  const handleDeleteCard = async () => {
    if (!selectedItem) return;
    try {
      const headers = {
        'X-CSRFToken': getCsrfToken(),
        'X-Organization-Id': currentOrganization.organization_id,
      };
      await axios.delete(`/api/tire-hotel-cards/${selectedItem.id}/`, { headers });
      setSuccessSnackbar(t('tire_hotel.card_deleted'));
      refetch();
      fetchCapacity();
    } catch (err) {
      console.error('Failed to delete card:', err);
    }
    setDeleteConfirmOpen(false);
    setSelectedItem(null);
  };

  const handleFormSaved = () => {
    setSuccessSnackbar(t('tire_hotel.card_saved'));
    refetch();
    fetchCapacity();
  };

  // Error snackbar
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const prevError = useRef(error);

  useEffect(() => {
    if (error && error !== prevError.current) {
      setShowErrorSnackbar(true);
    }
    prevError.current = error;
  }, [error]);

  return (
    <Box data-testid="tire-hotel-cards-page">
      <TrialFeatureNotice />
      {/* Error Snackbar */}
      <Snackbar
        open={showErrorSnackbar && !!error}
        autoHideDuration={6000}
        onClose={() => setShowErrorSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowErrorSnackbar(false)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successSnackbar}
        autoHideDuration={4000}
        onClose={() => setSuccessSnackbar(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessSnackbar(null)} severity="success" sx={{ width: '100%' }}>
          {successSnackbar}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            component="h4"
            variant="h4"
            data-testid="tire-hotel-cards-heading"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {t('tire_hotel.hotel_cards')}
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              color: 'text.secondary',
              mt: 0.5,
              display: { xs: 'none', sm: 'block' },
            }}
          >
            {t('tire_hotel.cards_subtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingCard(null);
            setFormDialogOpen(true);
          }}
          size="small"
          data-testid="add-hotel-card-button"
          sx={{
            borderRadius: '10px',
            fontWeight: 500,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {t('tire_hotel.new_card')}
        </Button>
      </Box>

      {/* Capacity Alert Banner */}
      {capacity.capacityAlert && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('tire_hotel.capacity_alert_message', {
            percentage: capacity.occupancyPercentage,
            threshold: capacity.alertThreshold,
          })}
        </Alert>
      )}

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Card sx={{ minWidth: 150, flex: 1 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="body2" color="text.secondary">
              {t('tire_hotel.total_cards')}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {totalCount}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 150, flex: 1 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="body2" color="text.secondary">
              {t('tire_hotel.occupied_locations')}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'warning.dark' }}>
              {capacity.occupied}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 150, flex: 1 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="body2" color="text.secondary">
              {t('tire_hotel.available_locations')}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'success.dark' }}>
              {capacity.available}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Search and Filter Row */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 2.5,
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        <SearchInput
          value={preferences.searchQuery}
          onChange={(value) => updatePreferences({ searchQuery: value })}
          onClear={() => updatePreferences({ searchQuery: '' })}
          placeholder={t('tire_hotel.cards_search_placeholder')}
          data-testid="tire-hotel-cards-search"
        />
        <FilterButton
          t={t}
          activeCount={activeFilterCount}
          isOpen={filtersOpen}
          onClick={toggleFiltersOpen}
        />
      </Box>

      {/* Active Filter Summary */}
      <FilterSummary chips={filterSummaryChips} onClear={resetFilters} t={t} />

      {/* Filter Panel */}
      <FilterPanel
        open={filtersOpen}
        resultCount={totalCount}
        onReset={resetFilters}
        resetDisabled={activeFilterCount === 0}
        t={t}
      >
        <FilterSection title={t('tire_hotel.status')}>
          {STATUSES.map((s) => (
            <FilterChip
              key={s}
              label={t(`tire_hotel.${s}`)}
              active={preferences.filters.status === s}
              onClick={() =>
                updateFilters({
                  status: preferences.filters.status === s ? '' : s,
                })
              }
              color={getStatusColor(s)}
            />
          ))}
        </FilterSection>
        <FilterSection title={t('tire_hotel.needs_replacement')}>
          <FilterChip
            label={t('tire_hotel.needs_replacement_only')}
            active={!!preferences.filters.needs_replacement}
            onClick={() => updateFilters({ needs_replacement: !preferences.filters.needs_replacement })}
            color="warning"
            data-testid="tire-hotel-filter-needs-replacement"
          />
        </FilterSection>
        <FilterSection title={t('tire_hotel.tire_set_type')}>
          {TIRE_SET_TYPES.map((type) => (
            <FilterChip
              key={type}
              label={t(`tire_hotel.${type}`)}
              active={preferences.filters.tire_set_type === type}
              onClick={() =>
                updateFilters({
                  tire_set_type: preferences.filters.tire_set_type === type ? '' : type,
                })
              }
            />
          ))}
        </FilterSection>
      </FilterPanel>

      {/* Desktop Table */}
      <DataTable
        columns={[
          {
            key: 'customer',
            label: t('tire_hotel.customer'),
            sortable: true,
            sortField: 'customer__name',
            maxWidth: 200,
            render: (item) => (
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {item.customer_name || '-'}
              </Typography>
            ),
          },
          {
            key: 'vehicle',
            label: t('tire_hotel.vehicle'),
            sortable: true,
            sortField: 'vehicle__registration_number',
            noWrap: true,
            render: (item) => (
              <Typography
                variant="body2"
                sx={{
                  ...plateFont,
                  color: 'text.secondary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.vehicle_registration_number || '-'}
              </Typography>
            ),
          },
          {
            key: 'tire_size',
            label: t('tire_hotel.tire_size'),
            render: (item) => item.tire_size_display || '-',
          },
          {
            key: 'type',
            label: t('Type'),
            render: (item) => (
              <Chip size="small" label={t(`tire_hotel.${item.tire_set_type}`)} sx={{ fontSize: '11px' }} />
            ),
          },
          {
            key: 'location',
            label: t('tire_hotel.location'),
            render: (item) => item.location_label || '-',
          },
          {
            key: 'status',
            label: t('tire_hotel.status'),
            sortable: true,
            sortField: 'status',
            render: (item) => (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  label={t(`tire_hotel.${item.status}`)}
                  color={getStatusColor(item.status)}
                  sx={{ fontSize: '11px' }}
                />
                <NeedsReplacementChip card={item} t={t} />
              </Box>
            ),
          },
          {
            key: 'tread_depth',
            label: t('tire_hotel.tread_depth'),
            render: (item) => (item.min_tread_depth ? `${item.min_tread_depth} mm` : '-'),
          },
          {
            key: 'actions',
            label: '',
            width: '1%',
            render: (item) => (
              <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton
                  size="small"
                  onClick={(e) => handleContextMenu(e, item)}
                  sx={{ color: 'text.disabled', '&:hover': { color: 'text.primary' } }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
            ),
          },
        ]}
        data={items}
        loading={loading}
        onRowClick={(item) => navigate(`/tire-hotel/${item.id}`)}
        sortField={preferences.sortField}
        sortDirection={preferences.sortDirection}
        onSort={handleSort}
        emptyIcon={<TireRepairIcon />}
        emptyTitle={t('tire_hotel.no_cards_found')}
        testIdPrefix="tire-hotel-card-row"
        tableTestId="tire-hotel-cards-table"
        pagination={{
          page,
          totalPages,
          totalCount,
          pageSize,
          onPageChange: setPage,
          onPageSizeChange: setPageSize,
        }}
      />

      {/* Mobile Cards */}
      <Box
        sx={{ display: { xs: 'block', lg: 'none' }, position: 'relative' }}
        data-testid="tire-hotel-cards-table-mobile"
      >
        {loading && items.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              pt: 8,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
        {loading && items.length === 0 ? (
          <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : items.length === 0 ? (
          <Paper elevation={0} sx={{ p: 4, textAlign: 'center' }}>
            <TireRepairIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary">
              {t('tire_hotel.no_cards_found')}
            </Typography>
          </Paper>
        ) : (
          items.map((item) => (
            <Paper
              key={item.id}
              elevation={0}
              data-testid={`tire-hotel-card-row-mobile-${item.id}`}
              onClick={() => navigate(`/tire-hotel/${item.id}`)}
              sx={{
                p: 2,
                mb: 1.5,
                cursor: 'pointer',
                transition: 'background-color 0.15s ease',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', gap: 1.5, flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      bgcolor: 'rgba(25, 118, 210, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'primary.main',
                      flexShrink: 0,
                    }}
                  >
                    <TireRepairIcon />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: '15px',
                        fontWeight: 600,
                        color: 'text.primary',
                      }}
                    >
                      {item.customer_name || '-'}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '13px',
                        color: 'text.secondary',
                        mt: 0.25,
                      }}
                    >
                      {item.vehicle_registration_number || ''} {item.tire_size_display ? `- ${item.tire_size_display}` : ''}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                      <Chip
                        size="small"
                        label={t(`tire_hotel.${item.status}`)}
                        color={getStatusColor(item.status)}
                        sx={{ fontSize: '11px' }}
                      />
                      <NeedsReplacementChip card={item} t={t} />
                      <Chip
                        size="small"
                        label={t(`tire_hotel.${item.tire_set_type}`)}
                        sx={{ fontSize: '11px' }}
                      />
                    </Box>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleContextMenu(e, item);
                  }}
                  sx={{
                    color: 'text.disabled',
                    '&:hover': { color: 'text.primary' },
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
            </Paper>
          ))
        )}
      </Box>

      {/* Mobile Pagination */}
      <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
        <PaginationControls
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          loading={loading}
        />
      </Box>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem
          onClick={() => {
            handleCloseContextMenu();
            if (selectedItem) navigate(`/tire-hotel/${selectedItem.id}`);
          }}
        >
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('View')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleCloseContextMenu();
            setDeleteConfirmOpen(true);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          <ListItemText>{t('tire_hotel.delete_card')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setSelectedItem(null);
        }}
      >
        <DialogTitle>{t('tire_hotel.delete_card')}</DialogTitle>
        <DialogContent>
          <Typography>{t('tire_hotel.confirm_delete')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteConfirmOpen(false);
              setSelectedItem(null);
            }}
          >
            {t('Cancel')}
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteCard}>
            {t('Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Form Dialog */}
      <TireHotelCardForm
        t={t}
        open={formDialogOpen}
        onClose={() => {
          setFormDialogOpen(false);
          setEditingCard(null);
        }}
        card={editingCard}
        onSaved={handleFormSaved}
      />
    </Box>
  );
}

export default withTranslation()(TireHotelCardsPage);
